import type { Accessor, Component, JSX } from 'solid-js'
import { createSignal, For, Show } from 'solid-js'
import { clickOutside } from '../../shared/directives/click-outside'
import { Icon } from '@iconify-icon/solid'
import { Trans, useTransContext } from '@mbarzda/solid-i18next'

interface FilterGroupProps {
  label: JSX.Element
  selectedCount: number
  open: Accessor<boolean>
  onMouseEnter: () => void
  onMouseLeave: () => void
  onToggle: () => void
  children: JSX.Element
}

const FilterGroup: Component<FilterGroupProps> = (props) => {
  return (
    <div onMouseEnter={props.onMouseEnter} onMouseLeave={props.onMouseLeave}>
      <button
        type="button"
        onClick={props.onToggle}
        class="flex w-full cursor-pointer items-center gap-1.5 rounded-md p-2 text-xs font-semibold uppercase tracking-wide text-primary-700 transition-colors hover:bg-primary-50"
      >
        <span class={`inline-flex transition-transform duration-200 ${props.open() ? 'rotate-90' : 'rotate-0'}`}>
          <Icon icon="heroicons:chevron-right" class="text-[0.75rem]" />
        </span>
        <span class="flex-1 text-left">{props.label}</span>
        <Show when={!props.open() && props.selectedCount > 0}>
          <span class="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-bold leading-none text-white">
            {props.selectedCount}
          </span>
        </Show>
      </button>
      <div
        class="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ 'grid-template-rows': props.open() ? '1fr' : '0fr' }}
      >
        <div class="overflow-hidden">
          <div class="p-1">{props.children}</div>
        </div>
      </div>
    </div>
  )
}

interface UserDirectorySubscriptionFilterProps {
  subscriptionPlans: string[]
  subscriptionLevels: string[]
  previousSubscriptionLevels: string[]
  subscriptionStatuses: string[]
  selectedPlans: string[]
  selectedLevels: string[]
  selectedPreviousLevels: string[]
  selectedStatuses: string[]
  onToggle: (type: 'plan' | 'level' | 'previousLevel' | 'status', values: string[]) => void
}

type GroupKey = 'plan' | 'level' | 'previousLevel' | 'status'
const DEFAULT_GROUP: GroupKey = 'plan'

const UserDirectorySubscriptionFilter: Component<UserDirectorySubscriptionFilterProps> = (props) => {
  const [t] = useTransContext()
  const [subscriptionOpen, setSubscriptionOpen] = createSignal(false)
  const [activeGroup, setActiveGroup] = createSignal<GroupKey>(DEFAULT_GROUP)
  let collapseTimeout: ReturnType<typeof setTimeout> | undefined

  const openGroup = (key: GroupKey) => {
    clearTimeout(collapseTimeout)
    setActiveGroup(key)
  }

  const scheduleDefault = () => {
    collapseTimeout = setTimeout(() => setActiveGroup(DEFAULT_GROUP), 200)
  }

  const toggleGroup = (key: GroupKey) => {
    clearTimeout(collapseTimeout)
    setActiveGroup((prev) => (prev === key ? DEFAULT_GROUP : key))
  }

  const isGroupOpen = (key: GroupKey) => () => activeGroup() === key

  const handleToggle = (type: GroupKey, value: string) => {
    const currentMap = {
      plan: props.selectedPlans,
      level: props.selectedLevels,
      previousLevel: props.selectedPreviousLevels,
      status: props.selectedStatuses
    }
    const current = currentMap[type]
    const newValues = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    props.onToggle(type, newValues)
  }

  const subscriptionLabel = () => {
    const count =
      props.selectedPlans.length +
      props.selectedLevels.length +
      props.selectedPreviousLevels.length +
      props.selectedStatuses.length
    if (count > 0) return t('ls_user_directory_subscription_type_count', { count })
    return t('ls_user_directory_subscription_type')
  }

  return (
    <div
      class="relative"
      ref={(el) => {
        clickOutside(el, () => setSubscriptionOpen(false))
      }}
    >
      <button
        type="button"
        onClick={() => {
          setSubscriptionOpen(!subscriptionOpen())
        }}
        class="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2"
      >
        <span class="whitespace-nowrap text-sm text-gray-700">{subscriptionLabel()}</span>
        <span class={`transition-transform duration-200 ${subscriptionOpen() ? 'rotate-180' : ''}`}>
          <Icon icon="heroicons:chevron-down" class="text-[1rem]" />
        </span>
      </button>
      <div
        class={`absolute right-0 top-full z-50 mt-1 w-56 origin-top rounded-lg border border-gray-200 bg-white shadow-lg transition-all duration-200 ease-out ${
          subscriptionOpen()
            ? 'pointer-events-auto scale-y-100 opacity-100'
            : 'pointer-events-none scale-y-95 opacity-0'
        }`}
      >
        <div class="max-h-72 overflow-y-auto p-1">
          <Show when={props.subscriptionPlans?.length > 0}>
            <FilterGroup
              label={<Trans key={'ls_user_directory_filters_plan'} />}
              selectedCount={props.selectedPlans.length}
              open={isGroupOpen('plan')}
              onMouseEnter={() => openGroup('plan')}
              onMouseLeave={scheduleDefault}
              onToggle={() => toggleGroup('plan')}
            >
              <For each={props.subscriptionPlans}>
                {(plan) => (
                  <label class="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={props.selectedPlans.includes(plan)}
                      onChange={() => handleToggle('plan', plan)}
                      class="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span class="text-gray-700">
                      {plan.toLowerCase().includes('year')
                        ? t('ls_generic_yearly')
                        : plan.toLowerCase().includes('month')
                        ? t('ls_generic_monthly')
                        : plan}
                    </span>
                  </label>
                )}
              </For>
            </FilterGroup>
          </Show>

          <FilterGroup
            label={<Trans key={'ls_user_directory_filters_level'} />}
            selectedCount={props.selectedLevels.length}
            open={isGroupOpen('level')}
            onMouseEnter={() => openGroup('level')}
            onMouseLeave={scheduleDefault}
            onToggle={() => toggleGroup('level')}
          >
            <For each={props.subscriptionLevels}>
              {(level) => (
                <label class="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={props.selectedLevels.includes(level)}
                    onChange={() => handleToggle('level', level)}
                    class="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span class="text-gray-700">{level}</span>
                </label>
              )}
            </For>
          </FilterGroup>

          <Show when={props.previousSubscriptionLevels?.length > 0}>
            <FilterGroup
              label={<Trans key={'ls_user_directory_filters_previous_level'} />}
              selectedCount={props.selectedPreviousLevels.length}
              open={isGroupOpen('previousLevel')}
              onMouseEnter={() => openGroup('previousLevel')}
              onMouseLeave={scheduleDefault}
              onToggle={() => toggleGroup('previousLevel')}
            >
              <For each={props.previousSubscriptionLevels}>
                {(level) => (
                  <label class="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={props.selectedPreviousLevels.includes(level)}
                      onChange={() => handleToggle('previousLevel', level)}
                      class="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span class="text-gray-700">{level}</span>
                  </label>
                )}
              </For>
            </FilterGroup>
          </Show>

          <Show when={props.subscriptionStatuses?.length > 0}>
            <FilterGroup
              label={<Trans key={'ls_user_directory_filters_status'} />}
              selectedCount={props.selectedStatuses.length}
              open={isGroupOpen('status')}
              onMouseEnter={() => openGroup('status')}
              onMouseLeave={scheduleDefault}
              onToggle={() => toggleGroup('status')}
            >
              <For each={props.subscriptionStatuses}>
                {(status) => (
                  <label class="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={props.selectedStatuses.includes(status)}
                      onChange={() => handleToggle('status', status)}
                      class="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span class="text-gray-700">{status}</span>
                  </label>
                )}
              </For>
            </FilterGroup>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default UserDirectorySubscriptionFilter
